/**
 * Ce qu'il manque : sur une fiche enfant, et sur le profil du responsable.
 *
 * ## Pourquoi un module, et pas un test dans chaque écran
 *
 * La même question se pose à quatre endroits — la page d'accueil (« qu'est-ce
 * que j'ai à faire ? »), la fiche de l'enfant (« qu'est-ce qui me bloque ? »),
 * le profil, et le panier qui refuse de valider. Elle était jusqu'ici tranchée
 * à un seul de ces endroits, et tard : le parent apprenait au moment de payer
 * que son profil était incomplet, après avoir choisi ses stages. Le refus
 * arrivait à l'instant où il coûtait le plus cher.
 *
 * Une réponse unique, posée une fois, permet de la dire tôt.
 *
 * ## Deux niveaux, et un seul bloque
 *
 * | | bloque l'inscription ? | se périme ? |
 * |---|---|---|
 * | Fiche enfant complète | oui | oui, à 6 semaines |
 * | Profil du responsable complet | oui | non |
 * | N° de registre national | non — signalé | non |
 *
 * Le numéro de registre national ne bloque rien : sans lui l'enfant est
 * accueilli, seule l'attestation fiscale manque. Le taire serait pourtant
 * malhonnête — 711 comptes et 876 enfants n'en ont pas, et leurs familles
 * découvriraient l'absence d'attestation en janvier. Il est donc *signalé*,
 * jamais opposé.
 *
 * ## Pourquoi la fiche se périme et pas le profil
 *
 * Une fiche enfant porte des allergies, un médecin, un niveau de natation, des
 * personnes autorisées à venir le chercher : autant de choses qui changent sans
 * que personne ne pense à les corriger. Une adresse et un téléphone de parent
 * changent aussi, mais leur péremption ne met personne en danger — et un
 * formulaire qui redemande tout tous les deux mois finit par être traversé sans
 * être lu, ce qui est pire que de ne pas demander.
 *
 * ## « Vérifier » veut dire « rattraper »
 *
 * Pour la base existante, ce module ne dit pas *relisez, c'est sans doute
 * bon* : 2 939 fiches sur 2 943 n'ont jamais été vérifiées, 4 seulement portent
 * des autorisations d'image, 1 128 n'ont pas de médecin. Ces fiches ont du
 * travail réel devant elles, pas une relecture. Les libellés d'ici servent à
 * l'annoncer honnêtement — « 3 informations à compléter » plutôt que
 * « vérifiez vos informations » — parce qu'une famille qui attend une
 * formalité et trouve un formulaire abandonne.
 */

import {
  depuisLignes,
  erreursEtape,
  ETAPES,
  type Etape,
  type LignesLues,
} from '../components/forms/kid/fiche';

/* -------------------------------------------------------------------------- */
/* La péremption                                                               */
/* -------------------------------------------------------------------------- */

/** Au-delà, une fiche enfant est à revérifier. */
export const VALIDITE_SEMAINES = 6;

const VALIDITE_MS = VALIDITE_SEMAINES * 7 * 24 * 60 * 60 * 1000;

/**
 * Vrai si la fiche doit être revue.
 *
 * `null` compte comme périmée, et ce n'est pas un détail : c'est le cas de
 * 2 939 fiches sur 2 943, celles d'avant l'existence de cette colonne. Les
 * traiter comme à jour reviendrait à ne jamais rien revérifier, c'est-à-dire à
 * ne rien faire du tout.
 */
export function estPerimee(derniereVerification: string | null | undefined, maintenant = new Date()): boolean {
  if (!derniereVerification) return true;
  const date = new Date(derniereVerification);
  if (Number.isNaN(date.getTime())) return true;
  return maintenant.getTime() - date.getTime() > VALIDITE_MS;
}

/** Depuis combien de jours la fiche a-t-elle été vérifiée ? `null` si jamais. */
export function joursDepuisVerification(
  derniereVerification: string | null | undefined,
  maintenant = new Date(),
): number | null {
  if (!derniereVerification) return null;
  const date = new Date(derniereVerification);
  if (Number.isNaN(date.getTime())) return null;
  return Math.floor((maintenant.getTime() - date.getTime()) / (24 * 60 * 60 * 1000));
}

/* -------------------------------------------------------------------------- */
/* Ce qui manque                                                               */
/* -------------------------------------------------------------------------- */

/**
 * `bloquant` empêche d'inscrire ; `signale` est dit sans rien empêcher.
 *
 * Confondre les deux a un coût dans les deux sens : un blocage de trop fait
 * abandonner, un blocage de moins laisse partir un enfant sans que l'équipe
 * sache quoi faire s'il fait une réaction allergique.
 */
export type Gravite = 'bloquant' | 'signale';

export interface Manque {
  /** Identifiant stable, pour les clés de liste et les tests. */
  cle: string;
  /** Ce que le parent lit. Une chose à faire, pas un nom de champ. */
  libelle: string;
  gravite: Gravite;
  /** Où le corriger, quand ça se situe dans une étape de la fiche. */
  etape?: Etape;
}

/** L'état d'une fiche enfant, tel qu'une pastille peut l'afficher. */
export type EtatFiche =
  /** Des informations bloquantes manquent. */
  | 'a-completer'
  /** Complète, mais plus vérifiée depuis plus de six semaines. */
  | 'a-verifier'
  /** Rien à faire. */
  | 'a-jour';

export interface CompletudeEnfant {
  manques: Manque[];
  bloquants: Manque[];
  signales: Manque[];
  /** Vrai si rien ne bloque l'inscription. */
  complet: boolean;
  perimee: boolean;
  etat: EtatFiche;
  /** Vrai si le parent a quelque chose à faire sur cette fiche. */
  aFaire: boolean;
  /** La première étape en défaut, pour y emmener directement. */
  premiereEtape: Etape | null;
}

/** Le titre d'une étape, pour nommer un manque par l'endroit où il se corrige. */
const TITRE_ETAPE: Record<Etape, string> = ETAPES.reduce(
  (acc, e) => ({ ...acc, [e.cle]: e.titre }),
  {} as Record<Etape, string>,
);

/**
 * Ce qui manque à une fiche enfant.
 *
 * Les règles ne sont pas réécrites ici : la fiche est relue vers le modèle du
 * formulaire, et ce sont ses propres validations qui répondent. Une seule
 * définition de « complet », donc — celle que le parent rencontrera s'il ouvre
 * le formulaire.
 */
export function completudeEnfant(lignes: LignesLues, maintenant = new Date()): CompletudeEnfant {
  const fiche = depuisLignes(lignes);
  const manques: Manque[] = [];

  for (const { cle } of ETAPES) {
    if (cle === 'recapitulatif') continue;
    const erreurs = erreursEtape(cle, fiche);
    for (const [champ, message] of Object.entries(erreurs)) {
      /*
        Un registre national enregistré mais incohérent (clé fausse, date qui
        ne correspond pas à la naissance) est SIGNALÉ, jamais bloquant.

        La règle s'est durcie le 22/10/2026 : elle confronte désormais le
        numéro à la date de naissance. Des fiches anciennes, importées, portent
        des numéros qui ne passent plus — et le bloquant les aurait fermées à
        toute inscription, pour une information qui ne décide que de
        l'attestation fiscale. Le formulaire, lui, refuse toujours d'en
        ENREGISTRER un faux et propose de continuer sans.
      */
      const nrn = cle === 'identite' && champ === 'nNational';
      manques.push({
        cle: `${cle}.${champ}`,
        libelle: nrn ? `Numéro de registre national à corriger : ${message}` : message,
        gravite: nrn ? 'signale' : 'bloquant',
        etape: cle,
      });
    }
  }

  /*
    Le registre national de l'enfant n'est pas dans les erreurs de l'étape :
    `erreursIdentite` ne se plaint que d'un numéro mal formé, jamais d'un
    numéro absent — c'est délibéré, il est facultatif. Mais son absence a une
    conséquence que la famille doit connaître avant janvier, alors on la dit
    ici, sans rien bloquer.
  */
  if (!fiche.identite.nNational.trim()) {
    manques.push({
      cle: 'identite.nNationalAbsent',
      libelle: 'Numéro de registre national manquant — sans lui, pas d’attestation fiscale.',
      gravite: 'signale',
      etape: 'identite',
    });
  }

  const bloquants = manques.filter((m) => m.gravite === 'bloquant');
  const signales = manques.filter((m) => m.gravite === 'signale');
  const complet = bloquants.length === 0;
  const perimee = estPerimee(lignes.kids?.last_verified_at, maintenant);

  return {
    manques,
    bloquants,
    signales,
    complet,
    perimee,
    etat: !complet ? 'a-completer' : perimee ? 'a-verifier' : 'a-jour',
    aFaire: !complet || perimee,
    premiereEtape: bloquants[0]?.etape ?? null,
  };
}

/** Le nombre de manques bloquants par étape, pour poser une pastille par section. */
export function manquesParEtape(completude: CompletudeEnfant): Partial<Record<Etape, number>> {
  const parEtape: Partial<Record<Etape, number>> = {};
  for (const m of completude.bloquants) {
    if (!m.etape) continue;
    parEtape[m.etape] = (parEtape[m.etape] ?? 0) + 1;
  }
  return parEtape;
}

/** Les étapes en défaut, nommées, pour une phrase du genre « Santé, Sorties ». */
export function etapesEnDefaut(completude: CompletudeEnfant): string[] {
  const vues = new Set<Etape>();
  const titres: string[] = [];
  for (const m of completude.bloquants) {
    if (!m.etape || vues.has(m.etape)) continue;
    vues.add(m.etape);
    titres.push(TITRE_ETAPE[m.etape]);
  }
  return titres;
}

/* -------------------------------------------------------------------------- */
/* Le profil du responsable                                                    */
/* -------------------------------------------------------------------------- */

/** Le profil, réduit aux champs qui décident de sa complétude. */
export interface ProfilLu {
  prenom?: string | null;
  nom?: string | null;
  adresse?: string | null;
  cpostal?: string | null;
  localite?: string | null;
  telephone?: string | null;
  nnational?: string | null;
  account_type?: string | null;
  organisation_name?: string | null;
}

export interface CompletudeProfil {
  manques: Manque[];
  bloquants: Manque[];
  signales: Manque[];
  complet: boolean;
  aFaire: boolean;
}

/** Les six champs exigés au panier, et leur nom tel que le parent le lit. */
const CHAMPS_PROFIL: { cle: keyof ProfilLu; libelle: string }[] = [
  { cle: 'prenom', libelle: 'Votre prénom' },
  { cle: 'nom', libelle: 'Votre nom' },
  { cle: 'adresse', libelle: 'Votre adresse' },
  { cle: 'cpostal', libelle: 'Votre code postal' },
  { cle: 'localite', libelle: 'Votre localité' },
  { cle: 'telephone', libelle: 'Votre téléphone' },
];

const rempli = (v: string | null | undefined) => !!v && v.trim().length > 0;

/**
 * Ce qui manque au profil du responsable.
 *
 * Les six champs bloquants sont exactement ceux que le panier refusait déjà :
 * l'objet n'est pas d'exiger davantage, c'est de le dire au début plutôt qu'à
 * la fin. Un parent renvoyé vers `/profile` après trois secondes de message
 * d'erreur, son panier rempli, est un parent qu'on a fait travailler pour rien.
 */
export function completudeProfil(profil: ProfilLu | null | undefined): CompletudeProfil {
  const manques: Manque[] = [];

  if (!profil) {
    manques.push({
      cle: 'profil.absent',
      libelle: 'Votre profil n’est pas encore complété.',
      gravite: 'bloquant',
    });
  } else {
    for (const champ of CHAMPS_PROFIL) {
      if (!rempli(profil[champ.cle] as string | null | undefined)) {
        manques.push({
          cle: `profil.${champ.cle}`,
          libelle: `${champ.libelle} est manquant.`,
          gravite: 'bloquant',
        });
      }
    }

    /*
      Une organisation n'a pas de prénom d'enfant à porter mais elle a un nom,
      et le formulaire du profil le refuse déjà vide. Ne le vérifier nulle part
      ailleurs laissait passer les comptes créés avant cette règle.

      Un compte « autre » n'est pas une organisation : c'est un proche
      (grand-parent, famille d'accueil, oncle…) qui inscrit l'enfant. Il n'a
      pas de nom d'organisation à donner — la fiche de l'équipe l'efface
      d'ailleurs pour lui (VALIDÉ ÉCLOSION, 22/10/2026).
    */
    const particulier = !profil.account_type || profil.account_type === 'parent';
    if (profil.account_type === 'organisation' && !rempli(profil.organisation_name)) {
      manques.push({
        cle: 'profil.organisation_name',
        libelle: 'Le nom de votre organisation est manquant.',
        gravite: 'bloquant',
      });
    }

    /*
      Le registre national n'est signalé qu'aux parents.

      Ce n'est pas seulement qu'une organisation n'en a pas : l'attestation
      fiscale n'est délivrée qu'au parent ou tuteur légal, et le formulaire du
      profil EFFACE le numéro dès que le compte n'est pas « parent ». Le
      réclamer à un proche ou à une organisation serait une demande sans objet
      — le genre de signal qu'on apprend à ignorer, et qui décrédibilise les
      autres.
    */
    if (particulier && !rempli(profil.nnational)) {
      manques.push({
        cle: 'profil.nnational',
        libelle: 'Numéro de registre national manquant — sans lui, pas d’attestation fiscale.',
        gravite: 'signale',
      });
    }
  }

  const bloquants = manques.filter((m) => m.gravite === 'bloquant');
  return {
    manques,
    bloquants,
    signales: manques.filter((m) => m.gravite === 'signale'),
    complet: bloquants.length === 0,
    aFaire: bloquants.length > 0,
  };
}

/* -------------------------------------------------------------------------- */
/* L'état d'ensemble                                                           */
/* -------------------------------------------------------------------------- */

export interface EnfantEvalue {
  id: string;
  prenom: string;
  completude: CompletudeEnfant;
}

export interface EtatCompte {
  profil: CompletudeProfil;
  enfants: EnfantEvalue[];
  /** Vrai s'il n'y a aucun enfant : l'accueil invite alors à en ajouter un. */
  sansEnfant: boolean;
  /** Les enfants sur lesquels il reste quelque chose à faire. */
  enfantsAFaire: EnfantEvalue[];
  /** Vrai si le parent peut inscrire : profil complet et au moins un enfant prêt. */
  peutInscrire: boolean;
  /** Le nombre total de choses à faire, profil compris. */
  nombreAFaire: number;
}

/**
 * L'état du compte, tel que la page d'accueil doit le raconter.
 *
 * `peutInscrire` demande un enfant prêt, pas tous : une famille de trois
 * enfants dont deux fiches sont à jour doit pouvoir les inscrire sans attendre
 * d'avoir réglé la troisième. Bloquer sur le tout ferait payer à deux enfants
 * le retard d'un seul.
 */
export function etatDuCompte(
  profil: ProfilLu | null | undefined,
  enfants: { id: string; lignes: LignesLues }[],
  maintenant = new Date(),
): EtatCompte {
  const etatProfil = completudeProfil(profil);
  const evalues: EnfantEvalue[] = enfants.map((e) => ({
    id: e.id,
    prenom: (e.lignes.kids?.prenom ?? '').trim(),
    completude: completudeEnfant(e.lignes, maintenant),
  }));

  const aFaire = evalues.filter((e) => e.completude.aFaire);

  return {
    profil: etatProfil,
    enfants: evalues,
    sansEnfant: evalues.length === 0,
    enfantsAFaire: aFaire,
    peutInscrire: etatProfil.complet && evalues.some((e) => !e.completude.aFaire),
    nombreAFaire: aFaire.length + (etatProfil.aFaire ? 1 : 0),
  };
}

/**
 * « 3 informations à compléter », « À vérifier », « À jour ».
 *
 * Le nombre compte parce qu'il borne l'effort : « vérifiez vos informations »
 * ne dit pas si c'est l'affaire de dix secondes ou d'un quart d'heure, et dans
 * le doute on remet à plus tard.
 */
export function resumeEnfant(c: CompletudeEnfant): string {
  if (c.bloquants.length > 0) {
    return c.bloquants.length === 1
      ? '1 information à compléter'
      : `${c.bloquants.length} informations à compléter`;
  }
  if (c.perimee) return 'À vérifier';
  return 'À jour';
}
